import Modal from 'components/Modal';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { THEME_OPTIONS } from '../ThemeCheckIn';
import IcTick from '../../assets/images/IcTick.png';

type Ref = {
  open: (theme: string) => void;
  close: () => void;
};

export type ChooseThemeCheckInModalProps = {
  onThemeChange: (theme: string) => void;
};

export const useChooseThemeCheckInModalRef = () => useRef<Ref>(null);

const ChooseThemeCheckInModal = forwardRef<Ref, ChooseThemeCheckInModalProps>(
  ({ onThemeChange }, ref) => {
    const [open, setOpen] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState<string | null>(null);

    const onOpen = (theme: string) => {
      setSelectedTheme(theme);
      setOpen(true);
    };

    const onClose = () => {
      setSelectedTheme(null);
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({
      open: (theme: string) => onOpen(theme),
      close: () => onClose(),
    }));

    return (
      <Modal
        title='Choose Theme Check-In'
        width='1200px'
        onCancel={onClose}
        open={open}
        onOk={() => {
          if (selectedTheme) {
            onThemeChange(selectedTheme);
          }
          onClose();
        }}
        closable={false}
        doneButtonTitle='OK'
      >
        <Container>
          {THEME_OPTIONS.map((theme) => (
            <ThemeCard
              key={theme.value}
              className={selectedTheme === theme.value ? 'selected' : ''}
              onClick={() => setSelectedTheme(theme.value)}
            >
              <img
                src={theme.image}
                alt={theme.label}
                style={{ width: '100%', height: '100%', borderRadius: '8px' }}
              />
              {selectedTheme === theme.value && (
                <Selected>
                  <img
                    src={IcTick}
                    alt='Selected'
                    style={{ width: '24px', height: '24px' }}
                  />
                </Selected>
              )}
            </ThemeCard>
          ))}
        </Container>
      </Modal>
    );
  },
);

ChooseThemeCheckInModal.displayName = 'ChooseThemeCheckInModal';

export default ChooseThemeCheckInModal;

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 20px;
  padding-top: 0;
`;

const ThemeCard = styled.div`
  position: relative;
  border: 1px solid #ccc;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #008bff;
  }

  &.selected {
    border-color: #008bff;
  }
`;

const Selected = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;
